import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box/box.component';

import { ConsolidatedForecastsListView } from '@futrli/views/views/forecasts/ConsolidatedForecastsListView/ConsolidatedForecastsListView';
import { ForecastsView } from '@futrli/views/views/forecasts/ForecastsView/ForecastsView';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { useNavHeight } from '@futrli/predict/hooks/useNavHeight';

import { MyForecastList } from '../components/my-forecast-list/my-forecast-list';
import { MyForecastsFilterBar } from '../components/my-forecasts-filter-bar/my-forecasts-filter-bar';
import { useMyForecastsFilters } from '../hooks/use-my-forecast-filters';

export const MyForecastsContainer = () => {
  const flags = useFlags();
  const isResourceSharingEnabled = !!flags.resourceSharing;
  const { navHeight } = useNavHeight();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const ViewClass = isConsolidation ? ConsolidatedForecastsListView : ForecastsView;
  const view = useApiView(
    useMemo(() => new ViewClass(workspaceId, organisationId, flags), [ViewClass, flags, workspaceId, organisationId])
  );
  const { forecasts, isLoading } = view.state;

  const {
    searchTerm,
    onSearchChange,
    statusFilterValue,
    onStatusFilterChange,
    typeShownFilterValue,
    onTypeShownFilterChange,
    sortByFilterValue,
    onSortByFilterChange,
    filteredForecasts
  } = useMyForecastsFilters(forecasts || [], organisationId, isResourceSharingEnabled);

  return (
    <>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="forecasting.dashboard.forecasts_list" defaultMessage="Forecasts list" />}
        helpUrl="https://support.futrli.com/en/articles/14301317-my-forecasts"
        mb={0}
      />
      <MyForecastsFilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        statusFilterValue={statusFilterValue}
        onStatusFilterChange={onStatusFilterChange}
        typeShownFilterValue={typeShownFilterValue}
        onTypeShownFilterChange={onTypeShownFilterChange}
        sortByFilterValue={sortByFilterValue}
        onSortByFilterChange={onSortByFilterChange}
        stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
        isResourceSharingEnabled={isResourceSharingEnabled}
      />
      <Box my={3}>
        <MyForecastList
          data={filteredForecasts}
          isConsolidation={isConsolidation}
          isLoading={isLoading}
          isEmpty={(forecasts || []).length === 0}
          isResourceSharingEnabled={isResourceSharingEnabled}
        />
      </Box>
    </>
  );
};
